import {
  FilterType, LinkedFilterSource, LinkedFilterOrigin, UserFilterPayloadType,
} from 'core/constants/filter';
import { ColumnDataTypes } from 'core/constants/report';
import { FilterOperatorTypes } from 'core/constants/report-expressions';
import { IFilterConfig, ILinkedFilterInfo } from 'core/models/report-response';
import { ReportReduxModel } from 'core/models';
import { IBuilderDimension } from 'core/models/report-builder/report-builder';
import {
  isValidArray, isValidArrayAndNotEmpty, isValidObject, truncateUUID,
} from 'components/feature/Report/ReportSidebar/common/helpers';
import { isLargeOptionSetFilter } from 'core/utils/common.utility';
import { isWrongInput } from 'core/utils/report.util';

export const FILTER_NAME_MAX_LENGTH = 40;

// Maps a FilterType to the set of compatible FilterType values for linking.
// Multiple keys can map to the same compatible set (e.g., all dropdown types share one set).
export const DROPDOWN_COMPATIBLE_TYPES = [
  FilterType.LSQMetadataMultiSelect,
  FilterType.LSQMetadataSingleSelect,
  FilterType.CustomDefinedMultiSelect,
  FilterType.CustomDefinedSingleSelect,
];

export const NUMBER_COMPATIBLE_TYPES = [FilterType.NumberInteger, FilterType.NumberDecimal];

export const CUSTOM_FILTER_TYPE_TO_COMPATIBLE_TYPES: Record<string, FilterType[]> = {
  [FilterType.DateRange]: [FilterType.DateRange],
  [FilterType.MultiTextSearch]: [FilterType.MultiTextSearch],
  [FilterType.UserMultiSelect]: [FilterType.UserMultiSelect],
  [FilterType.GroupMultiSelect]: [FilterType.GroupMultiSelect],
  [FilterType.NumberInteger]: NUMBER_COMPATIBLE_TYPES,
  [FilterType.NumberDecimal]: NUMBER_COMPATIBLE_TYPES,
  [FilterType.LSQMetadataMultiSelect]: DROPDOWN_COMPATIBLE_TYPES,
  [FilterType.LSQMetadataSingleSelect]: DROPDOWN_COMPATIBLE_TYPES,
  [FilterType.CustomDefinedMultiSelect]: DROPDOWN_COMPATIBLE_TYPES,
  [FilterType.CustomDefinedSingleSelect]: DROPDOWN_COMPATIBLE_TYPES,
};

// Maps a standalone filter's type to the canonical custom filter type.
// Used in seed mode to derive the custom filter type from the seed filter's type.
// e.g. LSQMetadataSingleSelect → LSQMetadataMultiSelect, NumberDecimal → NumberInteger.
export const mapToCustomFilterType = (type?: FilterType): FilterType => {
  if (!type) return FilterType.DateRange;
  if (DROPDOWN_COMPATIBLE_TYPES.includes(type)) return FilterType.LSQMetadataMultiSelect;
  if (NUMBER_COMPATIBLE_TYPES.includes(type)) return FilterType.NumberInteger;
  switch (type) {
    case FilterType.DateRange: return FilterType.DateRange;
    case FilterType.MultiTextSearch: return FilterType.MultiTextSearch;
    case FilterType.UserMultiSelect: return FilterType.UserMultiSelect;
    default: return FilterType.DateRange;
  }
};

// Converts a standalone filter into a linked filter entry for the constituent list.
// Used in seed mode when the custom filter editor is pre-populated from a standalone filter.
// Follows the same ruleset as the picker (handleLinkFilterFromTray):
//   QueryId set            → SubBuilder + AppliedFilter (query applied filter)
//   EntityName only        → MainReport + AppliedDimension (query applied dimension)
//   neither                → MainReport + AppliedFilter (main standalone)
export const buildSeedLinkedFilter = (seed: IFilterConfig): ILinkedFilterInfo => {
  const isQueryAppliedFilter = !!seed.QueryId;
  const isQueryAppliedDimension = !isQueryAppliedFilter && !!seed.EntityName;
  return {
    FilterId: seed.ID,
    Source: isQueryAppliedFilter ? LinkedFilterSource.SubBuilder : LinkedFilterSource.MainReport,
    QueryId: seed.QueryId,
    Origin: isQueryAppliedDimension ? LinkedFilterOrigin.AppliedDimension : LinkedFilterOrigin.AppliedFilter,
    LinkedDimensionId: seed.LinkedDimensionId,
  };
};

// Dropdown options for custom filter type selector.
// String, Dropdown and Number values use a canonical FilterType per group —
// MultiTextSearch for strings, LSQMetadataMultiSelect for dropdowns, and
// NumberInteger for numbers. Sub-types (e.g. LSQMetadataSingleSelect /
// CustomDefinedMultiSelect for dropdowns, NumberDecimal for numbers) fold into
// their group via CUSTOM_FILTER_TYPE_TO_COMPATIBLE_TYPES, so a single canonical
// value covers all compatible constituents. The Number group narrows to one
// concrete sub-type per custom filter — see Rule 6c in isFilterAvailableForLinking.
export const getSupportedCustomFilterTypes = (localize: (key: string) => any) => [
  { label: localize('reportBuilder.filterTypeDateRange'), value: FilterType.DateRange },
  { label: localize('reportBuilder.filterTypeString'), value: FilterType.MultiTextSearch },
  { label: localize('reportBuilder.filterTypeDropdown'), value: FilterType.LSQMetadataMultiSelect },
  { label: localize('reportBuilder.filterTypeNumber'), value: FilterType.NumberInteger },
  { label: localize('reportBuilder.filterTypeUser'), value: FilterType.UserMultiSelect },
];

// True when another filter in `filterConfig` already uses `filterName` (case-sensitive,
// trim-equal). The editing filter and the seed filter are excluded from the check —
// in edit mode the wrapper's own row would otherwise self-collide; in seed mode the
// standalone filter providing the initial name would do the same. Also excludes the
// pre-applied placeholder and removed rows, and ignores rows whose
// AssociatedCustomFilterId points to the editing filter (those are this filter's
// own constituents).
export const isCustomFilterNameAlreadyExists = (
  filterName: string,
  filterConfig: IFilterConfig[] | undefined,
  editingFilterId?: string,
  seedFilterId?: string,
): boolean => {
  const filtersToBeCheckedAgainst = filterConfig.filter((config) => {
    if (config.ID === editingFilterId || config.ID === seedFilterId || (!!config.AssociatedCustomFilterId && !config?.IsCustomFilter)) {
      return false;
    }
    return true;
  });
  return filtersToBeCheckedAgainst.some((filterConfigItem) => filterConfigItem?.Label?.trim() === filterName?.trim());
};

// Resolves the localized error message for the filter name input. Returns '' when valid.
// Precedence: duplicate name > too long > invalid characters.
export const getCustomFilterNameError = (
  filterName: string,
  hasDuplicate: boolean,
  localize: (key: string) => any,
): string => {
  if (hasDuplicate) return `*${localize('err.sameName')}`;
  if (filterName.length > FILTER_NAME_MAX_LENGTH) return `${localize('err.filterNameMaxLength')}`;
  if (isWrongInput(filterName)) return `${localize('err.invalidName')}`;
  return '';
};

// Builds a `{ queryId: queryName }` lookup from a DynamicQueryList. Empty when list is empty.
export const buildQueryNameMap = (
  dynamicQueryList: { Id: string; Name: string }[] | undefined,
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (isValidArrayAndNotEmpty(dynamicQueryList)) {
    dynamicQueryList.forEach((q) => { map[q.Id] = q.Name; });
  }
  return map;
};

// Resolves a linked filter constituent against the live picker pools.
// Preference order mirrors the picker dedup so the row label here matches what was shown:
//   1. SubBuilder applied filters — by exact ID, then by suffixed `<id>_<queryId>`.
//   2. Main report additionalFilters — strict (ID + LinkedDimensionId) when one was captured,
//      otherwise first ID match. Strict guards against same-ID variants under joined entities.
//   3. filterConfig fallback — covers seed mode where the standalone filter is applied but
//      hasn't surfaced in additionalFilters / subBuilderFilters yet.
export const findLinkedFilter = (
  filterId: string,
  queryId: string | undefined,
  linkedDimensionId: string | undefined,
  sources: {
    subBuilderFilters?: IFilterConfig[];
    additionalFilters?: IFilterConfig[];
    filterConfig?: IFilterConfig[];
  },
): IFilterConfig | undefined => {
  // Prefer SubBuilder applied filters first. They reflect live QueryPayload edits
  // (renames, metadata changes inside a query) whereas additionalFilters is cached
  // from get-additional-filters at report load and can carry stale Label / metadata
  // for the same field. Mirrors the picker's dedup preference — when both pools have
  // an entry for the same filter, the SubBuilder one wins so the row label here
  // matches what the picker showed.
  const { subBuilderFilters, additionalFilters, filterConfig } = sources;
  if (isValidArrayAndNotEmpty(subBuilderFilters)) {
    const exact = subBuilderFilters.find((f) => f.ID === filterId);
    if (exact) return exact;
    if (queryId) {
      const suffixed = subBuilderFilters.find((f) => f.ID === `${filterId}_${queryId}`);
      if (suffixed) return suffixed;
    }
  }

  // Fall back to main report additionalFilters. additionalFilters can contain MULTIPLE
  // entries with the same ID (the same Filter-Id surfacing once per joined entity —
  // e.g. User CreatedOn vs Lead CreatedOn). Constrain on LinkedDimensionId when the
  // picker captured one, otherwise this find returns the first array element and may
  // pick the wrong variant.
  const mainFilters = isValidArrayAndNotEmpty(additionalFilters) ? additionalFilters : [];
  const strictMatch = linkedDimensionId
    ? mainFilters.find((f) => f.ID === filterId && f.LinkedDimensionId === linkedDimensionId)
    : undefined;
  if (strictMatch) return strictMatch;
  const found = mainFilters.find((f) => f.ID === filterId);
  if (found) return found;
  // Final fallback: search filterConfig for applied standalone filters.
  // In seed mode, the seed filter is already applied in filterConfig but may not
  // exist in additionalFilters or subBuilderFilters, so this lookup is needed
  // to resolve its label and entity details in the linked filters list.
  return (filterConfig || []).find((f) => f.ID === filterId);
};

// Re-derives LinkedDimensionId for persisted entries that don't carry it (older saves,
// pre-feature). New picks already capture it; this keeps the strict-match path available
// across save + reload by sourcing the LinkedDimensionId from the live SubBuilder pool or
// filterConfig.
export const rehydrateLinkedDimensionIds = (
  initial: ILinkedFilterInfo[],
  subBuilderFilters: IFilterConfig[] | undefined,
  filterConfig: IFilterConfig[] | undefined,
): ILinkedFilterInfo[] => initial.map((lf) => {
  if (lf.LinkedDimensionId) return lf;
  const subEntry = subBuilderFilters?.find((f) => f.ID === lf.FilterId
    || (lf.QueryId ? f.ID === `${lf.FilterId}_${lf.QueryId}` : false));
  if (subEntry?.LinkedDimensionId) return { ...lf, LinkedDimensionId: subEntry.LinkedDimensionId };
  const cfgEntry = (filterConfig || []).find((f) => f.ID === lf.FilterId);
  if (cfgEntry?.LinkedDimensionId) return { ...lf, LinkedDimensionId: cfgEntry.LinkedDimensionId };
  return lf;
});

// Result of a calendar-date applicability check on a linked constituent:
//   isApplicable        — the constituent is Date-type and its linked dimension is applied.
//   calendarDateToggle  — the dimension's RangeFill, normalized to 1 (on) or 0 (off / absent).
export interface ICalendarDateApplicability {
  isApplicable: boolean;
  calendarDateToggle: number;
}

const NOT_APPLICABLE_CALENDAR_DATE: ICalendarDateApplicability = { isApplicable: false, calendarDateToggle: 0 };

// Normalizes a dimension's RangeFill into the calendar-date toggle: 1 when RangeFill === 1,
// 0 for anything else (missing path, null, 0, or any other value).
const toCalendarDateToggle = (rangeFill?: number | null): number => (rangeFill === 1 ? 1 : 0);

// Determines whether a filter being linked into a custom filter is backed by a
// currently-applied dimension, and whether that dimension has calendar-date (RangeFill)
// turned on. Only meaningful for Date-type (DateRange) filters — a date constituent can be
// sourced from an applied dimension, in which case its LinkedDimensionId points at a dimension
// in the report builder's appliedDimensions (updatedReportBuilderInfo.appliedDimensions).
// Matches the Id-based lookup used by selected-filter.component's edit-form.
// Main-builder filters only — query filters use hasQueryLinkedAppliedDimension.
export const hasLinkedAppliedDimension = (
  filter: IFilterConfig | undefined,
  appliedDimensions: ReportReduxModel.IDimension[] | undefined,
): ICalendarDateApplicability => {
  if (filter?.Type !== FilterType.DateRange || !filter?.LinkedDimensionId) return NOT_APPLICABLE_CALENDAR_DATE;
  const matchedDimension = (appliedDimensions || [])
    .filter((dim) => dim.Applied)
    .find((dimension) => dimension?.Id === filter.LinkedDimensionId);
  return {
    isApplicable: !!matchedDimension,
    calendarDateToggle: toCalendarDateToggle(matchedDimension?.Props?.Dimension?.RangeFill),
  };
};

// Query-filter variant of hasLinkedAppliedDimension. A query constituent's applied dimensions
// live in reportQueryBuilder.DynamicQueryList[queryId].QueryPayload.Select.Dimensions — every
// entry there is applied (it is the query's Select payload). The constituent's LinkedDimensionId
// can carry a trailing `_<queryId>` suffix, so strip it (truncateUUID) before matching the
// dimension's base Id — mirrors subQuery-filter.component's edit-form lookup. Note: an
// IBuilderDimension carries RangeFill at the top level (not under Props.Dimension like the
// report-redux IDimension), so the toggle reads dimension.RangeFill here.
export const hasQueryLinkedAppliedDimension = (
  filter: IFilterConfig | undefined,
  queryDimensions: IBuilderDimension[] | undefined,
): ICalendarDateApplicability => {
  if (filter?.Type !== FilterType.DateRange || !filter?.LinkedDimensionId) return NOT_APPLICABLE_CALENDAR_DATE;
  const linkedDimensionId = truncateUUID(filter.LinkedDimensionId);
  const matchedDimension = (queryDimensions || []).find((dimension) => dimension?.Id === linkedDimensionId);
  return {
    isApplicable: !!matchedDimension,
    calendarDateToggle: toCalendarDateToggle(matchedDimension?.RangeFill),
  };
};

// Decides whether a candidate filter should appear in the linkable pool for the
// custom filter currently being edited. The exclusion rules below mirror the
// ones documented on the caller (`getCompatibleFilters`):
//
//   1. Type mismatch        — filter type not in `compatibleTypes`.
//   2. Already linked       — present in `currentlyLinkedIds` (local form state).
//   3. Standalone applied   — present in `standaloneIds` (filterConfig without
//                             AssociatedCustomFilterId).
//   4. Linked elsewhere     — present in `linkedToOtherCustomFilterIds`.
//   5. Pre-associated row   — filter's AssociatedCustomFilterId is set. Allowed
//                             only when it points to the editing filter itself
//                             (freshly removed from the unsaved local state).
//   6. DataType mismatch    — Dropdown only; constituents share one DefaultValue,
//                             so candidates must match an already-linked DataType.
//   6b. LOS exclusivity     — An LOS candidate is allowed only when `areAllLOSFilters`
//                             is true (caller pre-computes this: dropdown type AND
//                             either no constituents linked yet OR every linked one
//                             is also LOS). Mixing LOS and non-LOS dropdowns in one
//                             custom filter breaks the value propagation in
//                             getUpdatedReportData since LOS serializes as
//                             { Type, Values } while non-LOS uses a flat array.
//   6c. Number sub-type     — Number only; once a Number constituent is linked,
//                             every candidate must share its concrete sub-type
//                             (NumberInteger vs NumberDecimal), so a Number custom
//                             filter stays homogeneous. Mirrors Rule 6 for dropdowns.
//   7. Stale sub-builder    — candidate carries a QueryId no longer reachable
//                             from the current data-source mapping.
export const isFilterAvailableForLinking = (
  filter: IFilterConfig,
  context: {
    compatibleTypes: FilterType[];
    currentlyLinkedIds: Set<string>;
    standaloneIds: Set<string>;
    linkedToOtherCustomFilterIds: Set<string>;
    editingFilterId?: string;
    isDropdownType: boolean;
    linkedDataTypes: Set<string>;
    areAllLOSFilters: boolean;
    isNumberType: boolean;
    linkedNumberTypes: Set<FilterType>;
    validQueryIds: Set<string>;
  },
): boolean => {
  const {
    compatibleTypes, currentlyLinkedIds, standaloneIds, linkedToOtherCustomFilterIds,
    editingFilterId, isDropdownType, linkedDataTypes, areAllLOSFilters,
    isNumberType, linkedNumberTypes, validQueryIds,
  } = context;

  if (!filter.Type || !compatibleTypes.includes(filter.Type)) return false;

  const ids = getIdVariants(filter.ID, filter.QueryId);
  const matchesAny = (set: Set<string>) => ids.some((id) => set.has(id));

  if (matchesAny(currentlyLinkedIds)) return false;
  if (matchesAny(standaloneIds)) return false;
  if (matchesAny(linkedToOtherCustomFilterIds)) return false;

  if (filter.AssociatedCustomFilterId) {
    return filter.AssociatedCustomFilterId === editingFilterId;
  }

  if (isDropdownType && linkedDataTypes.size > 0
      && (!filter.DataType || !linkedDataTypes.has(filter.DataType))) {
    return false;
  }

  if (isDropdownType && currentlyLinkedIds.size > 0
    && ((isLargeOptionSetFilter(filter) && !areAllLOSFilters)
  || (!isLargeOptionSetFilter(filter) && areAllLOSFilters))) return false;

  // Rule 6c: Number sub-type homogeneity (Number only). Once a Number constituent
  // is linked, every candidate must share its concrete sub-type (NumberInteger vs
  // NumberDecimal). Mirrors Rule 6 for dropdowns.
  if (isNumberType && linkedNumberTypes.size > 0 && !linkedNumberTypes.has(filter.Type)) {
    return false;
  }

  if (filter.QueryId && !validQueryIds.has(filter.QueryId)) return false;

  return true;
};

// The constituent a custom-filter wrapper "represents" for schema and dependency
// purposes: its FilterKey when set, otherwise the first linked constituent — mirroring
// buildCustomFiltersFromDefinitions' schemaConstituent choice. A child dropdown whose
// parent is a constituent of a wrapper should only inherit the wrapper's value when its
// parent IS this representative constituent; otherwise the wrapper no longer stands in
// for that parent and the dependency is broken.
export const getWrapperRepresentativeFilterId = (
  def: { FilterKey?: string; LinkedFilters?: { FilterId: string }[] } | undefined,
): string | undefined => def?.FilterKey || def?.LinkedFilters?.[0]?.FilterId;

// Sub-builder filter IDs can appear in two formats:
//   - Base ID:    "opportunities:mx_Custom_9-Filter-Id"
//   - Suffixed:   "opportunities:mx_Custom_9-Filter-Id_<queryId>"
// When matching a filter against exclusion sets, we must check both variants.
// Returns all possible ID forms for a given filter.
export const getIdVariants = (id: string, queryId?: string): string[] => {
  const variants = [id];
  if (queryId) {
    const suffixed = `${id}_${queryId}`;
    // If id already ends with the suffix, also add the base form
    if (id.endsWith(`_${queryId}`)) {
      variants.push(id.replace(`_${queryId}`, ''));
    } else {
      variants.push(suffixed);
    }
  }
  return variants;
};
